import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';

const PeriforApiEndpoint = `${environment.PeriforEndpoint}/api`;

@Injectable({
  providedIn: 'root'
})
export class ReportsService {

  constructor(private _http: HttpClient) { }

  /**
   * Download a report for a specific design
   * @param designId The design ID
   * @param reportEndpoint The report endpoint (e.g., 'perical-operations-summary')
   * @param options Optional configuration for perivis-results or perical-results reports
   * @returns A blob containing the report file
   */
  public async downloadReport(designId: string, reportEndpoint: string, options?: any): Promise<Blob> {
    // Combined report — POST with no designId in path; AppIds are set by the backend pipeline
    if (reportEndpoint === 'combined') {
      const url = `${PeriforApiEndpoint}/reports/combined-report`;
      return await lastValueFrom(
        this._http.post(url, options ?? null, { responseType: 'blob' })
      );
    }

    // For reports that have configurable options, use POST; otherwise use GET
    if ((reportEndpoint === 'perivis-results' || reportEndpoint === 'perical-results') && options) {
      const url = `${PeriforApiEndpoint}/reports/${reportEndpoint}/${designId}`;
      return await lastValueFrom(
        this._http.post(url, options, { responseType: 'blob' })
      );
    }
    
    // For perifor-inputs or other reports without path-based designId
    if (reportEndpoint === 'perifor-inputs' && options) {
      const url = `${PeriforApiEndpoint}/reports/${reportEndpoint}?designId=${designId}`;
      return await lastValueFrom(
        this._http.post(url, options, { responseType: 'blob' })
      );
    }
    
    const url = `${PeriforApiEndpoint}/reports/${reportEndpoint}?designId=${designId}`;
    return await lastValueFrom(
      this._http.get(url, { responseType: 'blob' })
    );
  }

  /**
   * Get Perical report headers (operation names, annulus names, etc.) for a design
   * @param designId The design ID
   * @returns Headers data for populating Perical report configuration dropdowns
   */
  public async getPericalHeaders(designId: string): Promise<any> {
    const url = `${PeriforApiEndpoint}/reports/perical-headers/${designId}`;
    return await lastValueFrom(
      this._http.get(url)
    );
  }

  /**
   * Get Perivis report headers (load case names) for a design
   * @param designId The design ID
   * @returns Headers data for populating Perivis report configuration dropdowns
   */
  public async getPerivisHeaders(designId: string): Promise<any> {
    const url = `${PeriforApiEndpoint}/reports/perivis-headers/${designId}`;
    return await lastValueFrom(
      this._http.get(url)
    );
  }
}
